import { FC, RefObject } from 'react';
import { CalendarTimeSlot } from './CalendarTimeSlot';
import { formatHour, formatDateNoYear } from '../../lib/helpers';
import { DAYS_OF_WEEK } from '../../lib/constants';
import type { UserAvailabilitySlot } from '@teachur/http-client';

interface CalendarGridProps {
  calendarRef: RefObject<HTMLDivElement | null>;
  weekDates: Date[];
  workHours: number[];
  weekStart: Date;
  availabilitySlots: UserAvailabilitySlot[];
  getSlotClassName: (dayIdx: number, hour: number) => string;
  handleMouseDown: (day: number, hour: number) => void;
  handleMouseEnter: (day: number, hour: number) => void;
  handleMouseUp: () => void;
  handleSlotClick: (slot: UserAvailabilitySlot) => void;
}

export const CalendarGrid: FC<CalendarGridProps> = ({
  calendarRef,
  weekDates,
  workHours,
  weekStart,
  availabilitySlots,
  getSlotClassName,
  handleMouseDown,
  handleMouseEnter,
  handleMouseUp,
  handleSlotClick,
}) => {
  return (
    <div
      ref={calendarRef}
      className="overflow-x-auto select-none"
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="min-w-200 select-none">
        <div className="grid grid-cols-[80px_repeat(7,1fr)] gap-px bg-(--neutral-black-NB200)">
          <div className="bg-(--neutral-white-NW1000) border-b border-(--neutral-black-NB200)" />
          {DAYS_OF_WEEK.map((day, idx) => (
            <div
              key={day}
              className="flex flex-col gap-1 bg-(--neutral-white-NW1000) py-3 px-2 text-center border-b border-(--neutral-black-NB200)"
            >
              <div className="[font-size:var(--fs-l4)] font-semibold text-(--neutral-black-NB900)">
                {day}
              </div>
              <div className="[font-size:var(--fs-l5)] text-(--neutral-white-NW400) m-0!">
                {formatDateNoYear(weekDates[idx])}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-[80px_repeat(7,1fr)] gap-px bg-(--neutral-black-NB200)">
          {workHours.map((hour) => (
            <div key={hour} className="contents">
              <div className="bg-(--neutral-white-NW1000) py-2 px-3 flex items-center justify-center [font-size:var(--fs-l5)] text-(--neutral-white-NW400) font-medium">
                {formatHour(hour)}
              </div>
              {DAYS_OF_WEEK.map((_, dayIdx) => (
                <CalendarTimeSlot
                  key={`${dayIdx}-${hour}`}
                  dayIdx={dayIdx}
                  hour={hour}
                  weekStart={weekStart}
                  availabilitySlots={availabilitySlots}
                  slotClassName={getSlotClassName(dayIdx, hour)}
                  onMouseDown={handleMouseDown}
                  onMouseEnter={handleMouseEnter}
                  onSlotClick={handleSlotClick}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
